import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { EditablePostClientStatus } from '../../../core/enums/editable-post-client-status';
import { GetProductionTechnicianAreaEnum, ProductionTechnicianArea, ProductionTechnicianAreaLabels } from '../../../core/enums/production-technician-area';
import { MachinePerformanceType, MachinePerformanceTypeLabels } from '../../../core/models/machine-performances/machine-performance-type';
import { MachineDetailsDto } from '../../../core/models/machines/machine-details-dto';
import { getMachineSizeFromLabel } from '../../../core/models/machines/machine-size';
import { MachinePerformanceApiService } from '../../../core/services/api/machine-performance-api.service';
import { SnackbarService } from '../../../core/services/error/snackbar.service';
import { MachineSetDefaultPerformanceAreaDialogComponent } from '../components/machine-set-default-performance-area-dialog/machine-set-default-performance-area-dialog.component';

@Injectable({
    providedIn: 'root'
})
export class MachinePerformanceService {

    constructor(
        private dialog: MatDialog,
        private apiService: MachinePerformanceApiService,
        private snackbar: SnackbarService
    ) { }

    /**
     * Orchestrates the entire "Set Default Performance" flow.
     * Returns true if changes were made.
     */
    public async promptAndApplyDefault(machine: MachineDetailsDto): Promise<boolean> {
        const teamAreas = this.getAvailableDefaultPerformanceAreas(machine);
        const allAreas = this.getAllProductionTechnicianAreas();

        const options = (teamAreas.length > 0 ? teamAreas : allAreas).map(area => ({
            value: area,
            label: ProductionTechnicianAreaLabels[area]
        }));

        const dialogRef = this.dialog.open(MachineSetDefaultPerformanceAreaDialogComponent, {
            data: {
                options: options,
                currentArea: machine.defaultMachinePerformanceArea ?? null
            },
            autoFocus: false,
            restoreFocus: false
        });

        const selectedArea = await firstValueFrom(dialogRef.afterClosed());

        if (selectedArea === null || selectedArea === undefined) {
            return false;
        }

        await this.applyArea(machine, selectedArea);
        return true;
    }

    public async applyArea(machine: MachineDetailsDto, area: ProductionTechnicianArea, notify = true): Promise<void> {
        const areaLabel = ProductionTechnicianAreaLabels[area];
        if (!areaLabel) return;

        const machineType = machine.baseInfo.machineType;
        const machineSizeText = machine.baseInfo.machineSizeText;

        if (!machineType || !machineSizeText) {
            return;
        }

        const machineSize = getMachineSizeFromLabel(machineSizeText);
        if (!machineSize) {
            return;
        }

        this.markExistingAsDeleted(machine);

        const performances = await firstValueFrom(
            this.apiService.getDefaultMachinePerformanceListByProductionTechnicianArea(
                areaLabel,
                machineType,
                machineSize
            )
        );

        machine.defaultMachinePerformanceArea = area;

        const newThinning = performances
            .filter(p => p.type === MachinePerformanceTypeLabels[MachinePerformanceType.Thinning])
            .map(p => ({ ...p, clientStatus: EditablePostClientStatus.New }));

        const newRegeneration = performances
            .filter(p => p.type === MachinePerformanceTypeLabels[MachinePerformanceType.Regeneration])
            .map(p => ({ ...p, clientStatus: EditablePostClientStatus.New }));

        machine.thinningPerformance = [...machine.thinningPerformance, ...newThinning];
        machine.regenerationPerformance = [...machine.regenerationPerformance, ...newRegeneration];

        if (notify) {
            this.snackbar.showResponse(`Maskinens område har ändrats till ${areaLabel}.`);
        }
    }

    private markExistingAsDeleted(machine: MachineDetailsDto) {
        machine.thinningPerformance.forEach(p => p.clientStatus = EditablePostClientStatus.Deleted);
        machine.regenerationPerformance.forEach(p => p.clientStatus = EditablePostClientStatus.Deleted);
    }

    private getAvailableDefaultPerformanceAreas(machine: MachineDetailsDto): ProductionTechnicianArea[] {
        if (!machine?.baseInfo?.teams?.length) return [];

        const areaSet = new Set<ProductionTechnicianArea>();
        machine.baseInfo.teams
            .filter(team => team.clientStatus !== EditablePostClientStatus.Deleted)
            .forEach(team => {
                if (team.productionTechnicianArea) {
                    const areaEnum = GetProductionTechnicianAreaEnum(team.productionTechnicianArea);
                    if (areaEnum !== ProductionTechnicianArea.Unknown) areaSet.add(areaEnum);
                }
            });
        return Array.from(areaSet.values()).sort((a, b) => a - b);
    }

    private getAllProductionTechnicianAreas(): ProductionTechnicianArea[] {
        return Object.values(ProductionTechnicianArea)
            .filter((v): v is ProductionTechnicianArea => typeof v === 'number' && v !== ProductionTechnicianArea.Unknown)
            .sort((a, b) => a - b);
    }
}
